import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Chip,
  Alert,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import ConfigEditorPage from './ConfigEditorPage';
import EventSystemTabLayout from '../../components/layout/EventSystemTabLayout';
import { useTenant } from '@libs/contexts/TenantContext';

/**
 * Config Workspace Page
 * Multi-tabbed workspace for editing device configurations
 * 
 * Features:
 * - Open multiple devices in separate tabs
 * - Persistent tab state (no reload on switch)
 * - Track unsaved changes per tab
 * - Session metadata (opened timestamp)
 * - Optional localStorage persistence
 */
export default function ConfigWorkspacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const processedUrlRef = useRef(false); // Track if we've processed URL params
  const { selectedTenant, loading: tenantLoading } = useTenant();
  const perfRef = useRef({ lastClick: null });
  const MemoConfigEditorPage = useMemo(() => React.memo(ConfigEditorPage), []);

  const isPerfDebugEnabled = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_CONFIG_WORKSPACE__ === true) return true;
      return localStorage.getItem('DEBUG_CONFIG_WORKSPACE') === 'true';
    } catch (_e) {
      return false;
    }
  }, []);
  
  // Workspace state
  const [activeTabId, setActiveTabId] = useState(null);
  const [tabs, setTabs] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState({}); // { tabId: boolean }

  // Load workspace from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('config_workspace_tabs');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.tabs && Object.keys(parsed.tabs).length > 0) {
          setTabs(parsed.tabs);
          setActiveTabId(parsed.activeTabId || Object.keys(parsed.tabs)[0]);
        }
      }
    } catch (err) {
      console.warn('Failed to restore workspace from localStorage:', err);
    }
  }, []);

  // Save workspace to localStorage whenever tabs change
  useEffect(() => {
    if (Object.keys(tabs).length > 0) {
      try {
        localStorage.setItem('config_workspace_tabs', JSON.stringify({
          activeTabId,
          tabs
        }));
      } catch (err) {
        console.warn('Failed to save workspace to localStorage:', err);
      }
    } else {
      localStorage.removeItem('config_workspace_tabs');
    }
  }, [tabs, activeTabId]);

  /**
   * Open a new device tab or switch to existing
   */
  const openDeviceTab = useCallback((device) => {
    const tabId = device.ip; // Use IP as unique tab ID
    
    if (tabs[tabId]) {
      // Tab already exists, just switch to it
      setActiveTabId(tabId);
    } else {
      // Create new tab
      const newTab = {
        deviceId: device.deviceId,
        name: device.name,
        ip: device.ip,
        model: device.model,
        openedAt: new Date().toISOString(),
        configData: null, // Will be loaded by ConfigEditorPage
        lastLoadedAt: null
      };
      
      setTabs(prev => ({ ...prev, [tabId]: newTab }));
      setActiveTabId(tabId);
    }
  }, [tabs]);

  // Handle opening a device from URL params (run only once per URL change)
  useEffect(() => {
    const deviceId = searchParams.get('deviceId');
    const deviceName = searchParams.get('deviceName');
    const deviceIp = searchParams.get('deviceIp');
    const model = searchParams.get('model');

    if (deviceId && deviceIp && !processedUrlRef.current) {
      processedUrlRef.current = true; // Mark as processed
      
      openDeviceTab({
        deviceId,
        name: deviceName || deviceId,
        ip: deviceIp,
        model: model || 'Unknown'
      });
      
      // Clear URL params after opening
      setSearchParams({}, { replace: true });
    } else if (!deviceId && !deviceIp) {
      // Reset flag when URL params are cleared
      processedUrlRef.current = false;
    }
  }, [searchParams, setSearchParams, openDeviceTab]);

  /**
   * Close a tab
   */
  const closeTab = useCallback((tabId, event) => {
    event?.stopPropagation();
    
    // Check for unsaved changes
    if (unsavedChanges[tabId]) {
      const confirmed = window.confirm(
        'This tab has unsaved changes. Are you sure you want to close it?'
      );
      if (!confirmed) return;
    }
    
    const tabIds = Object.keys(tabs);
    const currentIndex = tabIds.indexOf(tabId);
    
    // Remove tab
    const newTabs = { ...tabs };
    delete newTabs[tabId];
    setTabs(newTabs);
    
    // Remove unsaved changes flag
    const newUnsaved = { ...unsavedChanges };
    delete newUnsaved[tabId];
    setUnsavedChanges(newUnsaved);
    
    // Switch to adjacent tab if closing active tab
    if (activeTabId === tabId) {
      const remainingIds = Object.keys(newTabs);
      if (remainingIds.length > 0) {
        // Try to switch to next tab, or previous if at end
        const nextIndex = Math.min(currentIndex, remainingIds.length - 1);
        setActiveTabId(remainingIds[nextIndex]);
      } else {
        setActiveTabId(null);
      }
    }
  }, [tabs, activeTabId, unsavedChanges]);

  /**
   * Handle config data loaded
   */
  const handleConfigLoaded = useCallback((tabId, configData) => {
    setTabs(prev => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        configData,
        lastLoadedAt: new Date().toISOString()
      }
    }));
  }, []);

  /**
   * Handle unsaved changes flag
   */
  const handleUnsavedChanges = useCallback((tabId, hasChanges) => {
    setUnsavedChanges(prev => ({
      ...prev,
      [tabId]: hasChanges
    }));
  }, []);

  /**
   * Render tab label with close button
   */
  const renderTabLabel = (tabId, tab) => {
    const hasChanges = unsavedChanges[tabId];
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1,
          minWidth: 200,
          maxWidth: 300
        }}
      >
        {hasChanges && (
          <Tooltip title="Unsaved changes">
            <DotIcon sx={{ fontSize: 12, color: 'warning.main' }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {tab.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tab.ip}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => closeTab(tabId, e)}
          sx={{
            padding: 0.5,
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const tabIds = Object.keys(tabs);
  const activeTab = activeTabId ? tabs[activeTabId] : null;

  const handleTabChange = useCallback((_e, newValue) => {
    if (isPerfDebugEnabled()) {
      perfRef.current.lastClick = {
        from: activeTabId,
        to: newValue,
        t0: performance.now(),
      };
      try {
        window.__CONFIG_WS_LAST_CLICK__ = perfRef.current.lastClick;
      } catch (_e) {}
      console.info('[CONFIG_WS_PERF] tab_click', { from: activeTabId, to: newValue, t0: perfRef.current.lastClick.t0 });
    }
    setActiveTabId(newValue);
  }, [activeTabId, isPerfDebugEnabled]);

  useEffect(() => {
    if (!activeTabId) return;
    if (!isPerfDebugEnabled()) return;
    const last = perfRef.current.lastClick;
    if (!last || last.to !== activeTabId || typeof last.t0 !== 'number') {
      console.info('[CONFIG_WS_PERF] active_tab', { activeTabId, t: performance.now() });
      return;
    }

    const tEffect = performance.now();
    console.info('[CONFIG_WS_PERF] active_tab_effect', { from: last.from, to: last.to, dt_ms: tEffect - last.t0 });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tPaint = performance.now();
        console.info('[CONFIG_WS_PERF] active_tab_paint', { from: last.from, to: last.to, dt_ms: tPaint - last.t0 });
      });
    });
  }, [activeTabId, isPerfDebugEnabled]);

  return (
    <EventSystemTabLayout>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Configuration Workspace
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Edit multiple device configurations simultaneously
              </Typography>
            </Box>
            <Chip
              label={`${tabIds.length} device${tabIds.length !== 1 ? 's' : ''} open`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Tab Bar */}
        {tabIds.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'background.default'
            }}
          >
            <Tabs
              value={activeTabId}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 64,
                '& .MuiTab-root': {
                  minHeight: 64,
                  alignItems: 'flex-start',
                  textAlign: 'left'
                }
              }}
            >
              {tabIds.map(tabId => (
                <Tab
                  key={tabId}
                  value={tabId}
                  label={renderTabLabel(tabId, tabs[tabId])}
                />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {tabIds.length === 0 ? (
            // Empty state
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 600 }}>
                <Typography variant="h6" gutterBottom>
                  No devices open
                </Typography>
                <Typography variant="body2">
                  Open a device from the Devices page by clicking the gear icon (⚙️) in the Actions column.
                  Each device will open as a new tab here, allowing you to edit multiple configurations simultaneously.
                </Typography>
              </Alert>
              <IconButton
                color="primary"
                onClick={() => navigate('/admin/event-system/devices')}
                sx={{
                  width: 64,
                  height: 64,
                  border: 2,
                  borderColor: 'primary.main',
                  borderStyle: 'dashed'
                }}
              >
                <AddIcon fontSize="large" />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                Go to Devices page
              </Typography>
            </Box>
          ) : (
            // Active tab content
            tabIds.map(tabId => (
              <Box
                key={tabId}
                sx={{
                  display: activeTabId === tabId ? 'block' : 'none',
                  height: '100%',
                  overflow: 'auto'
                }}
              >
                <MemoConfigEditorPage
                  workspaceTabId={tabId}
                  deviceId={tabs[tabId].deviceId}
                  deviceName={tabs[tabId].name}
                  deviceIp={tabs[tabId].ip}
                  configData={tabs[tabId].configData}
                  lastLoadedAt={tabs[tabId].lastLoadedAt}
                  tenantId={selectedTenant?.id}
                  onConfigLoaded={handleConfigLoaded}
                  onUnsavedChanges={handleUnsavedChanges}
                  isWorkspaceMode={true}
                  isActive={activeTabId === tabId}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>
    </EventSystemTabLayout>
  );
}
